import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Megaphone, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import { useCreateSafetyCommunication } from '@/hooks/use-safety-communications';
import { useClauseStatuses } from '@/hooks/use-clause-status';
import { useOrganizationUsers } from '@/hooks/use-deployments';
import type {
  CommunicationCategory,
  TargetAudience,
  ClauseStatusDetail,
  OrgUserName,
} from '@/lib/types';

const CATEGORY_OPTIONS: { value: CommunicationCategory; label: string }[] = [
  { value: 'irata_bulletin', label: 'IRATA Bulletin' },
  { value: 'manufacturer_notice', label: 'Manufacturer Notice' },
  { value: 'internal_memo', label: 'Internal Memo' },
  { value: 'toolbox_update', label: 'Toolbox Update' },
  { value: 'other', label: 'Other' },
];

const TARGET_AUDIENCE_OPTIONS: { value: TargetAudience; label: string }[] = [
  { value: 'all_employees', label: 'All Employees' },
  { value: 'managers', label: 'Managers' },
  { value: 'supervisors', label: 'Supervisors' },
  { value: 'rope_access_technicians', label: 'Rope Access Technicians' },
  { value: 'custom', label: 'Custom' },
];

const QP300_CLAUSE_REFS = ['3.3.1', '3.3.2', '3.3.3'];

function UserLabel({ user }: { user: OrgUserName }) {
  return <span>{user.name}</span>;
}

export function CreateCommunicationPage() {
  const navigate = useNavigate();
  const createMutation = useCreateSafetyCommunication();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<CommunicationCategory>('other');
  const [sourceUrl, setSourceUrl] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [requiresAcknowledgment, setRequiresAcknowledgment] = useState(true);
  const [targetAudience, setTargetAudience] = useState<TargetAudience>('all_employees');
  const [relatedClauses, setRelatedClauses] = useState<number[]>([]);
  const [customRecipients, setCustomRecipients] = useState<number[]>([]);

  const { data: clauseStatuses, isLoading: clausesLoading } = useClauseStatuses({
    include_inapplicable: 'true',
  });
  const { data: users, isLoading: usersLoading } = useOrganizationUsers();

  const relatedClauseOptions = useMemo(() => {
    const seen = new Set<number>();
    return (clauseStatuses ?? [])
      .filter((c): c is ClauseStatusDetail & { clause_id: number } => {
        if (!QP300_CLAUSE_REFS.includes(c.clause_ref) || c.clause_id == null || seen.has(c.clause_id)) {
          return false;
        }
        seen.add(c.clause_id);
        return true;
      })
      .sort((a, b) => a.clause_ref.localeCompare(b.clause_ref));
  }, [clauseStatuses]);

  const setRelatedClause = (id: number, checked: boolean) => {
    setRelatedClauses((prev) =>
      checked
        ? prev.includes(id) ? prev : [...prev, id]
        : prev.filter((c) => c !== id)
    );
  };

  const setCustomRecipient = (id: number, checked: boolean) => {
    setCustomRecipients((prev) =>
      checked
        ? prev.includes(id) ? prev : [...prev, id]
        : prev.filter((r) => r !== id)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required.');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('body', body);
    formData.append('category', category);
    formData.append('requires_acknowledgment', String(requiresAcknowledgment));
    formData.append('target_audience', targetAudience);

    if (sourceUrl.trim()) {
      formData.append('source_url', sourceUrl);
    }
    if (attachment) {
      formData.append('attachment', attachment);
    }

    relatedClauses.forEach((id) => {
      formData.append('related_clauses', String(id));
    });

    if (targetAudience === 'custom') {
      customRecipients.forEach((id) => {
        formData.append('custom_recipients', String(id));
      });
    }

    try {
      const comm = await createMutation.mutateAsync(formData);
      if (!comm.sent_at) {
        toast.error('Communication created but response is missing sent_at.');
        return;
      }
      toast.success('Safety communication created.');
      navigate(`/compliance/communications/${comm.id}`);
    } catch {
      toast.error('Failed to create safety communication.');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/compliance/communications">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Safety Communication</h1>
          <p className="text-gray-600 mt-1">
            Distribute IRATA bulletins, manufacturer notices, or internal safety memos
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Communication Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., IRATA Safety Bulletin 2026-01"
                  required
                />
              </div>

              <div>
                <Label htmlFor="body">Body *</Label>
                <Textarea
                  id="body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter the communication content..."
                  rows={6}
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={category}
                  onValueChange={(v) => setCategory(v as CommunicationCategory)}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="source_url">Source URL</Label>
                <Input
                  id="source_url"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label htmlFor="attachment">Attachment</Label>
                <div className="mt-1 border-2 border-dashed rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <input
                    id="attachment"
                    type="file"
                    className="hidden"
                    onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  />
                  <label
                    htmlFor="attachment"
                    className="cursor-pointer text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {attachment ? attachment.name : 'Click to select a file'}
                  </label>
                  {attachment && (
                    <p className="text-xs text-gray-500 mt-1">
                      {(attachment.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Audience & Related Clauses</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="target_audience">Target Audience *</Label>
                <Select
                  value={targetAudience}
                  onValueChange={(v) => setTargetAudience(v as TargetAudience)}
                >
                  <SelectTrigger id="target_audience">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_AUDIENCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {targetAudience === 'custom' && (
                <div>
                  <Label>Custom Recipients</Label>
                  {usersLoading ? (
                    <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading users...
                    </div>
                  ) : (users ?? []).length === 0 ? (
                    <p className="text-sm text-gray-500 py-3">No users available.</p>
                  ) : (
                    <div className="mt-2 max-h-48 overflow-y-auto space-y-2 border rounded-md p-3">
                      {(users ?? []).map((user) => (
                        <div key={user.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`recipient-${user.id}`}
                            checked={customRecipients.includes(user.id)}
                            onCheckedChange={(checked) =>
                              setCustomRecipient(user.id, checked === true)
                            }
                          />
                          <Label
                            htmlFor={`recipient-${user.id}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            <UserLabel user={user} />
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start gap-3">
                <Checkbox
                  id="requires_acknowledgment"
                  checked={requiresAcknowledgment}
                  onCheckedChange={(checked) =>
                    setRequiresAcknowledgment(checked === true)
                  }
                />
                <div>
                  <Label htmlFor="requires_acknowledgment" className="text-sm font-normal">
                    Requires employee acknowledgment
                  </Label>
                  <p className="text-xs text-gray-500">
                    Targeted users must acknowledge they have read this communication.
                  </p>
                </div>
              </div>

              <div>
                <Label>Related QP-300 Clauses</Label>
                {clausesLoading ? (
                  <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading clauses...
                  </div>
                ) : relatedClauseOptions.length === 0 ? (
                  <p className="text-sm text-gray-500 py-3">
                    No QP-300 3.3.x clauses available.
                  </p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {relatedClauseOptions.map((clause) => (
                      <div key={clause.clause_id} className="flex items-start gap-2">
                        <Checkbox
                          id={`clause-${clause.clause_id}`}
                          checked={relatedClauses.includes(clause.clause_id)}
                          onCheckedChange={(checked) =>
                            setRelatedClause(clause.clause_id, checked === true)
                          }
                        />
                        <Label
                          htmlFor={`clause-${clause.clause_id}`}
                          className="text-sm font-normal cursor-pointer leading-tight"
                        >
                          <span className="font-medium">Clause {clause.clause_ref}</span>
                          <span className="text-gray-500 block">{clause.clause_title}</span>
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/compliance/communications')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            <Megaphone className="h-4 w-4 mr-2" />
            Create Communication
          </Button>
        </div>
      </form>
    </div>
  );
}
